/**
 * Comprehensive domain models for the Zeettu application
 * All interfaces match the backend domain model specifications
 */

// Base entity interface with common fields
export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
  whoAdded: string;
}

// Employee role enum
export enum Role {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  OFFICE = 'office',
  Z_REP = 'z_rep',
  I_REP = 'i_rep',
  PROCUREMENT = 'procurement',
  STORE_MANAGER = 'store_manager',
  /** POS-only manager limited to their assigned outlets. */
  OUTLET_MANAGER = 'outlet_manager',
  CASHIER = 'cashier',
  DRIVER = 'driver',
  WAREHOUSE = 'warehouse',
  ACCOUNTANT = 'accountant',
  RECOVERY_OFFICER = 'recovery_officer',
}

// Employee model
export interface Employee extends BaseEntity {
  name: string;
  mobileNumber: string;
  employeeId?: string;
  companyId: string;
  role: Role;
  isActive: boolean;
  lastLoginAt?: string;
  // Additional employee fields for HR module
  firstName?: string;
  lastName?: string;
  landNumber?: string;
  dob?: string;
  idNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  isMarried?: boolean;
  isMale?: boolean;
  roles?: Role[];
  profilePicture?: string;
  /** True when a picture was uploaded as a file; fetch it via `employeesService.getProfilePictureUrl`. */
  hasUploadedPicture?: boolean;
  /** Outlets (branchIds) a cashier may sell from; empty means unrestricted. */
  assignedBranchIds?: string[];
  /** Terminals a cashier may sell from; empty means unrestricted. */
  assignedTerminalIds?: string[];
  hasPosPin?: boolean;
}

// Emergency contact for customers
export interface EmergencyContact {
  name: string;
  mobileNumber: string;
  relationship: string;
}

/** Service types the customer has used (true = customer uses that service) */
export interface CustomerServices {
  zeettu?: boolean;
  installment?: boolean;
  retail?: boolean;
  wholesale?: boolean;
  other?: boolean;
}

// Customer model with embedded emergency contacts
export interface Customer extends BaseEntity {
  customerId: string;
  companyId?: string;
  firstName: string;
  lastName: string;
  /** Created at the POS with only a name, phone and route. */
  incomplete?: boolean;
  mobileNumber: string;
  landNumber?: string;
  email?: string;
  dob?: string;
  nic?: string;
  passport?: string;
  drivingLicense?: string;
  idNumber?: string;
  routeId: string;
  address?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  village?: string;
  location?: string;
  isMarried?: boolean;
  isMale?: boolean;
  zeettuActive: boolean;
  emergencyContacts?: EmergencyContact[];
  notes?: string;
  profilePicture?: string;
  nicFrontImage?: string;
  nicBackImage?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  whoUpdated?: string;
  referance?: string;
  /** Loyalty / accept-return credit points balance. May be negative. */
  creditPoints?: number;
  /** Service types the customer has used */
  services?: CustomerServices;
}

// Item model for inventory
export interface Item extends BaseEntity {
  _id: string;
  itemId: string;           // Auto-generated (e.g., "I25A0001")
  itemName: string;
  type: string;             // e.g., "Smartphone", "Laptop", "Electronics"
  brand: string;
  model: string;
  sellingPrice?: number;    // Optional
  markedPrice: number;      // Required
  buyingPrice?: number;     // Optional
  discount?: number;        // Optional
  downPayment?: number;
  retailSalePrice?: number;
  WholeSalePrice?: number;
  paddleSalePrice?: number;
  onlineSalePrice?: number;
  installmentCount?: number;
  installmentOption1?: number[];
  installmentOption2?: number[];
  installmentOption3?: number[];
  minStock?: number;
  maxCommision?: number;
  image: string;            // Image URL (e.g., "/uploads/items/uuid.webp")
  whoUpdated?: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  companyId: string;
  
  // Legacy/compatibility fields
  name?: string;
  description?: string;
  category?: string;
  unitPrice?: number;
  stockQuantity?: number;
  stockCount?: number;
  minStockLevel?: number;
  isActive?: boolean;
  isBundled?: boolean;
  isSold?: boolean;
  isReturned?: boolean;
  sku?: string;
  barcode?: string;
  imagePath?: string;
  itemBatchId?: string;
  sellerId?: string;
}

// Bundle model for grouped items
export interface Bundle extends BaseEntity {
  name: string;
  description: string;
  items: {
    itemId: string;
    quantity: number;
    unitPrice: number;
  }[];
  totalPrice: number;
  isActive: boolean;
  validFrom?: string;
  validTo?: string;
}

// Employee attendance record
export interface Attendance extends BaseEntity {
  employeeId: string;
  date: string;
  checkIn?: string;
  checkOut?: string;
  status: 'present' | 'absent' | 'late' | 'half-day';
  review?: string;
  notes?: string;
}

// Employee leave request
export interface Leave extends BaseEntity {
  employeeId: string;
  startDate: string;
  endDate: string;
  type: 'sick' | 'vacation' | 'personal' | 'emergency';
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedBy?: string;
  approvedAt?: string;
  note?: string;
  notes?: string;
}

// Employee salary record
export interface Salary extends BaseEntity {
  employeeId: string;
  month: string;
  year: number;
  basicSalary: number;
  baseSalary?: number;
  allowances: number;
  deductions: number;
  netSalary: number;
  netPaid?: number;
  status: 'pending' | 'paid';
  paidAt?: string;
  paymentDate?: string;
  notes?: string;
}

// Zeettu card model
export interface ZeettuCard extends BaseEntity {
  customerId: string;
  cardNumber: string;
  isActive: boolean;
  issuedAt: string;
  expiresAt?: string;
  balance: number;
  creditLimit: number;
}

// Delivery package model
export interface DeliveryPackage extends BaseEntity {
  customerId: string;
  items: {
    itemId: string;
    quantity: number;
    unitPrice: number;
  }[];
  totalAmount: number;
  status: 'pending' | 'delivered' | 'cancelled';
  deliveryDate: string;
  deliveredAt?: string;
  notes?: string;
}

// Sample record model
export interface SampleRecord extends BaseEntity {
  customerId: string;
  itemId: string;
  quantity: number;
  issuedAt: string;
  returnedAt?: string;
  status: 'out' | 'returned';
  notes?: string;
}

// Purchase summary model
export interface PurchaseSummary {
  customerId: string;
  totalPurchases: number;
  totalAmount: number;
  lastPurchaseDate: string;
  averageOrderValue: number;
  favoriteItems: string[];
}

// Purchase trip model
export interface PurchaseTrip extends BaseEntity {
  purchaseTripId: string;
  empId: string;
  whoUpdated: string;
  vehicleId: string;
  purchaseReportId: string;
  status?: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  isActive?: boolean;
}

// Store model
export interface Store extends BaseEntity {
  name: string;
  address: string;
  phone: string;
  email?: string;
  isActive: boolean;
  managerId: string;
}

// Audit log model
export interface AuditLog extends BaseEntity {
  entityType: string;
  entityId: string;
  action: 'create' | 'update' | 'delete' | 'view';
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  ipAddress: string;
  userAgent: string;
}

// Role permissions model
export interface RolePermissions {
  role: string;
  permissions: string[];
  isActive: boolean;
}

// Authentication response model
export interface AuthResponse {
  status: string;
  message: string;
  data: {
    employeeId: string;
    _id: string;
    createdAt: string;
    updatedAt: string;
    firstName: string;
    lastName: string;
    isAdmin: boolean;
    isFullTime: boolean;
    isActive: boolean;
    companyId: string;
    status: string;
    mobileNumber: string;
    idToken: string;
    role?: Role;
    roles?: Role[];
  };
}

// Paginated response wrapper
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// API error model
export interface APIError {
  message: string;
  status: number;
  code?: string;
  details?: Record<string, unknown>;
}

/** @deprecated Use DashboardSummaryResponse */
export interface ZeettuSummary {
  totalCustomers: number;
  pendingDeliveries: number;
  monthlyCollections: number;
  totalSamplesOut: number;
  stockValue: number;
  activeZeettuCards: number;
  activeInstallmentCards: number;
  totalRevenue: number;
  averageOrderValue: number;
}

export enum DashboardPeriod {
  TODAY = 'today',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
  ALL_TIME = 'all_time',
}

export interface DashboardMetric<T = number> {
  value: T;
  period: DashboardPeriod;
  range?: { from?: string; to?: string };
}

export interface DashboardSummaryMetrics {
  totalCustomers: DashboardMetric;
  pendingDeliveries: DashboardMetric;
  collections: DashboardMetric;
  samplesOut: DashboardMetric;
  activeZeettuCards: DashboardMetric;
  activeInstallmentCards: DashboardMetric;
  stockValue: DashboardMetric;
  averageOrderValue: DashboardMetric;
}

export interface DashboardSummaryResponse {
  generatedAt: string;
  metrics: DashboardSummaryMetrics;
}

export type DashboardActivityKind =
  | 'zdr'
  | 'pdr'
  | 'irr'
  | 'str'
  | 'purchase'
  | 'grn'
  | 'replacement_note'
  | 'service_receipt';

export interface DashboardActivityItem {
  id: string;
  kind: DashboardActivityKind;
  title: string;
  description: string;
  timestamp: string;
  user: string;
  status: string;
  href: string;
}

export type DashboardMetricKey = keyof DashboardSummaryMetrics;

export const DASHBOARD_METRIC_PERIOD_PARAMS: Record<DashboardMetricKey, string> = {
  totalCustomers: 'totalCustomersPeriod',
  pendingDeliveries: 'pendingDeliveriesPeriod',
  collections: 'collectionsPeriod',
  samplesOut: 'samplesOutPeriod',
  activeZeettuCards: 'activeZeettuCardsPeriod',
  activeInstallmentCards: 'activeInstallmentCardsPeriod',
  stockValue: 'stockValuePeriod',
  averageOrderValue: 'averageOrderValuePeriod',
};

// Dashboard KPI card data
export interface KPICard {
  title: string;
  value: string | number;
  change?: number;
  changeType?: 'increase' | 'decrease' | 'neutral';
  icon?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}

// Recent activity item for dashboard
export interface RecentActivity {
  id: string;
  type: 'delivery' | 'sample' | 'purchase' | 'customer' | 'item';
  title: string;
  description: string;
  timestamp: string;
  user: string;
  status?: string;
}

// Item API response types
export interface ItemCreateResponse {
  success: boolean;
  message: string;
  data: {
    itemId: string;
    message: string;
    _id: string;
    image: string;
  };
}

export interface ItemResponse {
  success: boolean;
  data: Item;
  message?: string;
}

export interface ItemsListResponse {
  success: boolean;
  data: {
    items: Item[];
    pagination: {
      page: number;
      size: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface ItemStatsResponse {
  success: boolean;
  data: {
    totalItems: number;
    totalPages: number;
    itemsByType: Record<string, number>;
    itemsByBrand: Record<string, number>;
    averageSellingPrice: number;
    averageMarkedPrice: number;
  };
}

export interface ItemCreateRequest {
  itemName: string;
  type: string;
  brand: string;
  model: string;
  markedPrice: number;
  whoAdded: string;
  sellingPrice?: number;
  buyingPrice?: number;
  discount?: number;
  downPayment?: number;
  retailSalePrice?: number;
  WholeSalePrice?: number;
  paddleSalePrice?: number;
  onlineSalePrice?: number;
  installmentCount?: number;
  installmentOption1?: number[];
  installmentOption2?: number[];
  installmentOption3?: number[];
  minStock?: number;
  maxCommision?: number;
  image?: string;
}

export interface ItemUpdateRequest {
  itemName?: string;
  type?: string;
  brand?: string;
  model?: string;
  sellingPrice?: number;
  markedPrice?: number;
  buyingPrice?: number;
  discount?: number;
  downPayment?: number;
  retailSalePrice?: number;
  WholeSalePrice?: number;
  paddleSalePrice?: number;
  onlineSalePrice?: number;
  installmentCount?: number;
  installmentOption1?: number[];
  installmentOption2?: number[];
  installmentOption3?: number[];
  minStock?: number;
  maxCommision?: number;
  image?: string;
  whoUpdated?: string;
  updatedAt?: string;
}

export interface ItemSearchParams {
  page?: number;
  size?: number;
  searchTerm?: string;
  type?: string;
  brand?: string;
  model?: string;
  whoAdded?: string;
  whoUpdated?: string;
  minSellingPrice?: number;
  maxSellingPrice?: number;
  minMarkedPrice?: number;
  maxMarkedPrice?: number;
  sortBy?: string;
  sortDirection?: 1 | -1;
}

// Seller model
export interface Seller extends BaseEntity {
  _id: string;
  sellerId: string;           // Auto-generated (e.g., "SL25A0001")
  sellerName: string;
  mobileNumber1: string;
  mobileNumber2?: string;
  location: string;
  remark?: string;
  whoUpdated?: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  companyId: string;
}

// Seller API response types
export interface SellerCreateResponse {
  success: boolean;
  message: string;
  data: {
    sellerId: string;
    message: string;
    _id: string;
  };
}

export interface SellerResponse {
  success: boolean;
  data: Seller;
}

export interface SellersListResponse {
  success: boolean;
  data: {
    sellers: Seller[];
    pagination: {
      page: number;
      size: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface SellerCreateRequest {
  sellerName: string;
  mobileNumber1: string;
  mobileNumber2?: string;
  location: string;
  remark?: string;
  whoAdded: string;
}

export interface SellerUpdateRequest {
  sellerName?: string;
  mobileNumber1?: string;
  mobileNumber2?: string;
  location?: string;
  remark?: string;
  whoUpdated?: string;
  updatedAt?: string;
  status?: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
}

export interface SellerSearchParams {
  page?: number;
  size?: number;
  searchTerm?: string;
  sellerId?: string;
  sellerName?: string;
  mobileNumber1?: string;
  mobileNumber2?: string;
  location?: string;
  whoAdded?: string;
  whoUpdated?: string;
  sortBy?: string;
  sortDirection?: 1 | -1;
}

// Customer API request/response types
export interface CustomerCreateRequest {
  firstName: string;
  lastName: string;
  mobileNumber: string;
  landNumber?: string;
  dob: string;
  nic?: string;
  passport?: string;
  drivingLicense?: string;
  addressLine1: string;
  addressLine2?: string;
  village: string;
  city: string;
  location?: string;
  routeId: string;
  isMarried: boolean;
  isMale: boolean;
  whoAdded?: string;
  referance?: string;
  creditPoints?: number;
  services?: CustomerServices;
}

export interface CustomerUpdateRequest {
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  landNumber?: string;
  dob?: string;
  nic?: string;
  passport?: string;
  drivingLicense?: string;
  addressLine1?: string;
  addressLine2?: string;
  village?: string;
  city?: string;
  location?: string;
  routeId?: string;
  isMarried?: boolean;
  isMale?: boolean;
  whoUpdated?: string;
  referance?: string;
  creditPoints?: number;
  services?: CustomerServices;
}

export interface CustomerListParams {
  page?: number;
  size?: number;
  searchTerm?: string;
  /** Comma-separated field keys limiting text search (backend $or). */
  searchFields?: string;
  sortBy?: string;
  sortDirection?: 1 | -1;
  routeId?: string;
}

export interface CustomerResponse {
  success: boolean;
  message?: string;
  data: Customer;
}

export interface CustomerListResponse {
  success: boolean;
  message?: string;
  data: {
    customers: Customer[];
    pagination: {
      page: number;
      size: number;
      total: number;
      totalPages: number;
    };
  };
}

// InstallmentCard model
export interface InstallmentCard extends BaseEntity {
  _id: string;
  installmentCardId: string;           // Auto-generated (e.g., "IC25A0001")
  pdrId?: string;
  customerId: string;
  stockId?: string[];
  routeId?: string;
  itemId?: string[];
  dealValue: number;
  balance?: number;                   // Remaining amount owed; starts at dealValue on create; decreases by payment amount
  creditValue?: number;               // Pool for shortfalls; 0 on create
  basePayment: number;
  installmentValue: number;
  installmentCount: number;
  paymentDate?: number;
  downPayment?: number; // Custom down payment (if different from item's default)
  remainingInstallmentCount?: number;
  startDate?: string;
  location: string;
  installmentCardFrontImage?: string;
  installmentCardBackImage?: string;
  /** User-facing remark (e.g. close / IRR notes); merged on admin close */
  remark?: string;
  /** IAC reference consumed from source document pool when applicable */
  reference?: string;
  whoUpdated?: string;
  /** Resolved on list endpoint from customers collection */
  customerDisplayName?: string;
  /** Backend may set CANCELED when an installment card is administratively closed. */
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED' | 'CANCELED' | 'PENDING';
  /** Employee ID who approved the card. */
  approvedBy?: string;
  /** ISO timestamp when the card was approved. */
  approvedAt?: string;
  /** Employee ID of the collector assigned to this card. */
  assignedCollector?: string;
  /** Whether down-payment (DP) commission has been paid. */
  isDBCpaid?: boolean;
  /** Whether the down-payment installment has been collected. */
  isDPcollected?: boolean;
  /** ISO timestamp when the card was administratively closed. */
  cancelledAt?: string;
  /** Reason recorded when the card was closed. */
  closeReason?: 'PAYMENTS_COMPLETED' | 'ITEMS_RETURNED' | 'GIVEUP';
  companyId: string;
  lastPaymentDate?: string;
}

// InstallmentCard API response types
export interface InstallmentCardCreateResponse {
  success: boolean;
  message: string;
  data: {
    installmentCardId: string;
    message: string;
    _id: string;
  };
}

export interface InstallmentCardResponse {
  success: boolean;
  data: InstallmentCard;
}

/** Slim row returned for installment card list tables (all tabs). */
export type InstallmentCardListRow = Pick<
  InstallmentCard,
  | 'installmentCardId'
  | 'customerId'
  | 'reference'
  | 'dealValue'
  | 'balance'
  | 'createdAt'
  | 'isDPcollected'
  | 'isDBCpaid'
  | 'approvedBy'
  | 'assignedCollector'
  | 'routeId'
  | 'status'
>;

/** @deprecated Use InstallmentCardListRow */
export type PendingApprovalInstallmentCardRow = InstallmentCardListRow;

export interface PendingApprovalListTotals {
  dealValueSum: number;
  balanceSum: number;
}

export interface InstallmentCardsListResponse {
  success: boolean;
  data: {
    installmentCards: InstallmentCard[];
    pagination: {
      page: number;
      size: number;
      total: number;
      totalPages: number;
    };
    totals?: PendingApprovalListTotals;
  };
}

export interface InstallmentCardCreateRequest {
  pdrId?: string;
  customerId: string;
  stockId?: string[];
  routeId?: string;
  itemId?: string[];
  dealValue?: number;
  basePayment?: number;
  installmentValue?: number;
  installmentCount?: number;
  location: string;
  installmentCardFrontImage?: File;
  installmentCardBackImage?: File;
  /** IAC reference number from availableNumbers (server resolves source document) */
  reference?: string;
  assignedCollector?: string;
}

export interface InstallmentCardUpdateRequest {
  pdrId?: string;
  customerId?: string;
  stockId?: string[];
  routeId?: string;
  itemId?: string[];
  dealValue?: number;
  balance?: number;
  creditValue?: number;
  basePayment?: number;
  installmentValue?: number;
  installmentCount?: number;
  location?: string;
  /** IAC reference number from availableNumbers (server resolves source document) */
  reference?: string;
  installmentCardFrontImage?: File;
  installmentCardBackImage?: File;
  whoUpdated?: string;
  assignedCollector?: string;
  isDBCpaid?: boolean;
  updatedAt?: string;
  status?: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  /** Replaces the card remark when sent (prefer append UX on the client). */
  remark?: string;
  /** Server-side append to existing card remark (preferred over `remark` overwrite). */
  remarkAppend?: string;
}

export interface InstallmentCardSearchParams {
  page?: number;
  size?: number;
  searchTerm?: string;
  /** Comma-separated field keys limiting text search (backend $or). */
  searchFields?: string;
  customerId?: string;
  stockId?: string;
  routeId?: string;
  whoAdded?: string;
  /** Employee ID assigned as collector on the card */
  assignedCollector?: string;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  /** Card startDate lower bound (YYYY-MM-DD or ISO) */
  startDateFrom?: string;
  /** Card startDate upper bound (YYYY-MM-DD or ISO) */
  startDateTo?: string;
  /** Document status filter (e.g. ACTIVE, CANCELED) — must match backend DocumentStatus */
  status?: string;
  /** When true, only ACTIVE cards with empty approvedBy */
  pendingApproval?: boolean;
  /** When true, only ACTIVE approved cards with isDBCpaid false or unset */
  pendingDpCommission?: boolean;
  /** When true, only ACTIVE cards with non-empty approvedBy */
  approvedOnly?: boolean;
  /** Filter by down-payment collected flag (true / false). */
  isDPcollected?: boolean;
  location?: string;
  sortBy?: string;
  sortDirection?: 1 | -1;
  /** When false, skip footer sum aggregation (faster for infinite-scroll pages). */
  includeTotals?: boolean;
}

// Stock model
export interface Stock extends BaseEntity {
  _id: string;
  stockId: string;           // Auto-generated (e.g., "SKU25A00001")
  itemId: string;
  itemName: string;
  quantity: number;
  stockStatus: 'ordered' | 'purchased' | 'stored' | 'reserved' | 'peddling' | 'transport' | 'delivered' | 'sold' | 'returned' | 'combined';
  content?: string[];
  /** Physical condition (backend `Condition` enum). */
  condition?: string;
  type?: string;
  brand?: string;
  brandId?: string;
  color?: string;
  remark?: string;
  basePayment?: number;
  buyingPrice: number;
  markedPrice?: number;
  dispatchReportId?: string;
  storeReportId?: string;
  storeId?: string;
  sellerId?: string;
  image?: string;
  returnReportId?: string;
  purchaseReportId?: string;
  purchaseTripId?: string;
  routeId?: string;
  whoUpdated?: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  companyId: string;
}